import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { attentionBudget } from '@cwos/api-client';
import { ConsequenceNote, ErrorPanel, Pill, SkeletonSet } from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { SettingsCard, SettingsHeader } from './common.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * How much Phibian may interrupt one person in a day.
 *
 * ---------------------------------------------------------------------------
 * THE SCREEN COMPOSES NO SENTENCE ABOUT WHAT THE NUMBER MEANS
 * ---------------------------------------------------------------------------
 * Founder closeout decision 3 of 2026-09-08 requires a tenant-configurable
 * value to carry explicit semantics — unit, time basis, behaviour — DERIVABLE
 * rather than restated in each place. A screen that wrote "interruptions per
 * person per day" into its own JSX would be the second statement of that, and
 * the one that drifts. So the server sends `effectiveDescription` and this
 * renders it; the only prose here is about CONSEQUENCE, which is the thing a
 * screen is actually for.
 *
 * ---------------------------------------------------------------------------
 * THE THREE STATES THIS SCREEN MUST NOT COLLAPSE
 * ---------------------------------------------------------------------------
 *   ABSENT       nobody ever set a budget. NO BUDGET IS APPLIED. Never shown as
 *                "0", which would tell an administrator their organization is
 *                silenced when it is not.
 *   WITHDRAWN    somebody set one and turned it off. Also no budget applied,
 *                and still a different fact — the record of what was in force
 *                is what makes a deferral from last Tuesday explicable.
 *   IN FORCE     a number, and possibly a note that the platform's delivery
 *                ceiling cut it down. That ceiling is a safety control, not an
 *                opinion about attention, and it is shown as its own thing.
 */
export function AttentionBudgetSettings() {
  const qc = useQueryClient();
  const { has } = usePermissions();
  const canManage = has('insights:attention_budget');

  const view = useQuery({
    queryKey: ['insights', 'attention-budget'],
    queryFn: () => attentionBudget.read(),
  });

  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (cap: number | null) => attentionBudget.declare({ cap }),
    onSuccess: () => {
      setError(null);
      setDraft('');
      void qc.invalidateQueries({ queryKey: ['insights', 'attention-budget'] });
    },
    // A REFUSAL IS NOT A RETRY. The server's `message` already says what to do
    // instead — withdraw rather than set zero, come down under the ceiling — so
    // it is shown verbatim and no "try again" is offered.
    onError: (err: unknown) => setError(messageOf(err)),
  });

  if (view.isLoading) return <SkeletonSet surface="table" rows={3} />;
  if (view.isError && !view.data) {
    return (
      <LoadFailure
        error={view.error}
        message="We couldn’t load the attention budget just now."
        onRetry={() => void view.refetch()}
      />
    );
  }

  const data = view.data!;
  const inForce = data.budget.basis === 'org_rule' ? data.budget : null;
  const everDeclared = data.provenance !== null;
  const typed = draft.trim();
  const parsed = Number(typed);
  const canSave = typed !== '' && Number.isInteger(parsed) && parsed > 0;

  return (
    <div className="rn-col" style={{ maxWidth: 760 }}>
      <SettingsHeader
        title="Attention budget"
        blurb="Phibian watches everything and speaks rarely. This is how rarely — for one person, in one day."
      />

      {error ? <ErrorPanel message={error} /> : null}

      <SettingsCard>
        <h2 className="rn-card-title">What is in force</h2>
        <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-10)' }}>
          {inForce === null ? (
            <>
              <Pill variant="good" dot>
                No budget applied
              </Pill>
              <p className="rn-muted">
                {everDeclared
                  ? // WITHDRAWN, not absent, and the difference is stated rather
                    // than left to a missing pill.
                    'This organization had a budget and withdrew it. Nothing is being held back; the ' +
                    'record of what was in force is kept, so a notification that waited last week can ' +
                    'still be explained.'
                  : 'This organization has never set one, so nothing is held back. That is not a budget ' +
                    'of zero — it is no budget at all, and every finding that is worth an interruption ' +
                    'still makes one.'}
              </p>
            </>
          ) : (
            <>
              {/* THE SERVER'S OWN SENTENCE. Not composed here. */}
              <strong>{data.effectiveDescription}</strong>
              {inForce.clamped === 'delivery_ceiling' ? (
                <ConsequenceNote
                  text={
                    `You asked for ${inForce.declared}. The delivery system carries at most ` +
                    `${data.deliveryCeiling.description}, which is a platform safety limit rather ` +
                    'than an opinion about attention, so that is what is in force.'
                  }
                />
              ) : null}
            </>
          )}
        </div>
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">Waiting on attention</h2>
        <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-10)' }}>
          {/* DEFERRED ITEMS REMAIN OBSERVABLE — the ruling, as a number
              somebody can act on. A budget with a growing backlog behind it is
              a budget that is too tight, and this is where that shows. */}
          <span>
            {data.deferred.obligations === 0
              ? 'Nothing is waiting.'
              : `${data.deferred.obligations} ${
                  data.deferred.obligations === 1 ? 'obligation is' : 'obligations are'
                } waiting for room in somebody’s day.`}
          </span>
          <p className="rn-muted">
            Nothing here has been dropped. Every one of these is still an open finding at its own
            severity, still owed to the person it names, and the next day’s sweep reconsiders it.
            Urgent findings are never held back, whatever this number says.
          </p>
        </div>
      </SettingsCard>

      {canManage ? (
        <SettingsCard>
          <h2 className="rn-card-title">Change it</h2>
          <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-12)' }}>
            <label style={{ display: 'grid', gap: 'var(--space-6)' }}>
              <span className="rn-muted">
                {/* Unit and window come from the server, so the label cannot
                    disagree with the value above it. */}
                Most{' '}
                {data.semantics.unit === 'interruption' ? 'interruptions' : data.semantics.unit} one
                person may receive in one organization day
              </span>
              <input
                style={{ marginBottom: 0 }}
                type="number"
                min={1}
                max={data.deliveryCeiling.limit}
                aria-label="Attention budget"
                value={draft}
                placeholder={inForce === null ? 'not set' : String(inForce.declared)}
                onChange={(e) => setDraft(e.target.value)}
              />
            </label>
            <div style={{ display: 'flex', gap: 'var(--space-10)', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="signin-devbtn"
                style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                disabled={save.isPending || !canSave}
                onClick={() => save.mutate(parsed)}
              >
                {save.isPending ? 'Saving…' : 'Set the budget'}
              </button>
              {inForce !== null ? (
                <button
                  type="button"
                  className="rn-btn-quiet"
                  disabled={save.isPending}
                  onClick={() => save.mutate(null)}
                >
                  Withdraw the budget
                </button>
              ) : null}
            </div>
            <ConsequenceNote
              text={
                'Setting this postpones interruptions; it never cancels them. A finding that could not be ' +
                'delivered today is still open, still at its own severity, still owed to the person it ' +
                'names, and is offered again the next day. To stop rationing altogether, withdraw the ' +
                'budget — there is no zero, because a zero would be silence wearing a budget’s name.'
              }
            />
          </div>
        </SettingsCard>
      ) : null}
    </div>
  );
}

/** The server's sentence when there is one; a plain fallback when there is not. */
function messageOf(err: unknown): string {
  if (err !== null && typeof err === 'object' && 'message' in err) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim() !== '') return message;
  }
  return 'That did not save.';
}
